// ** React Imports
import { useState, useEffect, MouseEvent, useCallback, ReactElement } from 'react'

// ** MUI Imports
import Box from '@mui/material/Box'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import { DataGrid, GridEventListener } from '@mui/x-data-grid';
import Typography from '@mui/material/Typography'
import Icon from 'src/features/common/CustomIcon'
import Link from '@mui/material/Link';
// ** Store Imports
import { SelectOption } from 'src/types/common';
import RefreshTwoToneIcon from '@mui/icons-material/RefreshTwoTone';
// ** Custom Components Imports
import Tooltip from '@mui/material/Tooltip'
import { client } from 'src/utils/axios-client'
import { leadEndpoints } from 'src/configs/endpoints'

import _ from 'lodash'
import { ActivitySummaryDashboardResponse, ActivityType, CadenceListDialogStore, DashboardTodoStore, DashboardViewStore, InboundCallStore, Lead, LeadListStore, LeadScreenStore, QuickLeadDialogStore, ViewType } from '../../stores/LeadStore'
import { ActivityColor, colorDateTooltipOverdue, colorDateTooltipOverdueDashboard, nextDateDays, nextDateDaysDashboard } from '../../utils/date-utils'
import VoicemailTwoToneIcon from '@mui/icons-material/VoicemailTwoTone';
import ArrowUpwardTwoToneIcon from '@mui/icons-material/ArrowUpwardTwoTone';
import ArrowDownwardTwoToneIcon from '@mui/icons-material/ArrowDownwardTwoTone';
import TrendingUpTwoToneIcon from '@mui/icons-material/TrendingUpTwoTone';
import TrendingDownTwoToneIcon from '@mui/icons-material/TrendingDownTwoTone';
import StandardSkeleton from '../common/StandardSkeleton';
import CardContent from '@mui/material/CardContent'
import Skeleton from '@mui/material/Skeleton'
import Avatar from '@mui/material/Avatar';
import { Solid } from 'mdi-material-ui';
import PerformanceCard from '../common/PerformanceCard';


type Props = {
    activityDate: string,
    title: string,
    userId: string
}

type DashboardStats = {
    dateRange: string,
    totalActivities: number,
    totalMeaningfulActivities: number,
    totalMqlActivities: number,
    totalDiscoveryCalls: number,
    totalOpportunityActivities: number,
    totalActivitiesDifference: number,
    totalMeaningfulDifference: number,
    totalMqlDifference: number,
    totalDiscoveryCallDifference: number,
    totalOpportunityDifference: number,
    comparedTo: string,
    totalActivitiesDif: string,
    totalMIActivitiesDif: string,
    totalMQLActivitiesDif: string,
    totalDCActivitiesDif: string,
    totalOpportunityActivitiesDif: string
}
const DashboardPerformance = ({ activityDate, title, userId }: Props) => {
    //const activitySummary = ActivitySummaryDashboardResponse.useState(s => s.record);
    const [reloadRecords, setReloadRecords] = useState<boolean>(false)
    const [dataLoaded, setDataLoaded] = useState<boolean>(false)
    const [activitySummary, setActivitySummary] = useState<DashboardStats>({} as DashboardStats)

    useEffect(() => {
        loadRecords(activityDate, title, userId);
    }, [reloadRecords, userId])

    const loadRecords = async (activityDate: string, title: string, userId: string) => {
        //setDataLoaded(false);
        const response = await client.get(leadEndpoints.getactivityperformance, { params: { activityDate, userId } });
        setActivitySummary(response.data);
        setDataLoaded(true);
    }

     if (dataLoaded) {
        return (
            <Grid container spacing={2} justifyContent={'center'} >
                <Grid item xs={12} md={12} sx={{ display: 'flex', alignItems: 'left', flexDirection: 'column', width: '100%' }}>
                    <Card >
                        <Typography sx={{ fontSize: 16, color: 'DarkGrey', ml: 3, mr:3, width: '100%' }}>
                            {title}
                        </Typography>
                    </Card>
                </Grid>
                <Grid item xs={12} md={2.4} alignItems="flex-start" spacing={1}>
                    <Card>
                        <PerformanceCard totalActivities={activitySummary.totalActivities} labelText={"Total Activities"}
                            diffType={activitySummary.totalActivitiesDif} totalDifference={activitySummary.totalActivitiesDifference}
                            comparedTo={activitySummary.comparedTo} activityDate={activityDate} activityType={"activity"} />
                    </Card>
                </Grid>
                <Grid item xs={12} md={2.4} alignItems="flex-start" spacing={1}>
                    <Card>
                        <PerformanceCard totalActivities={activitySummary.totalMeaningfulActivities} labelText={"Total Meaningful Activities"}
                            diffType={activitySummary.totalMIActivitiesDif} totalDifference={activitySummary.totalMeaningfulDifference}
                            comparedTo={activitySummary.comparedTo} activityDate={activityDate} activityType={"mi"} />    
                    </Card>
                </Grid>
                <Grid item xs={12} md={2.4} alignItems="flex-start" spacing={1}>
                    <Card>
                        <PerformanceCard totalActivities={activitySummary.totalMqlActivities} labelText={"Total MQL Activities"}
                            diffType={activitySummary.totalMQLActivitiesDif} totalDifference={activitySummary.totalMqlDifference}
                            comparedTo={activitySummary.comparedTo} activityDate={activityDate} activityType={"mql"}/>    
                    </Card>
                </Grid>
                <Grid item xs={12} md={2.4} alignItems="flex-start" spacing={1}>
                    <Card>
                        <PerformanceCard totalActivities={activitySummary.totalDiscoveryCalls} labelText={"Total Discovery Calls"}
                            diffType={activitySummary.totalDCActivitiesDif} totalDifference={activitySummary.totalDiscoveryCallDifference}
                            comparedTo={activitySummary.comparedTo} activityDate={activityDate} activityType={"dc"}/>    
                    </Card>
                </Grid>
                <Grid item xs={12} md={2.4} alignItems="flex-start" spacing={1}>
                    <Card>
                        <PerformanceCard totalActivities={activitySummary.totalOpportunityActivities} labelText={"Total Opportunities"}
                            diffType={activitySummary.totalOpportunityActivitiesDif} totalDifference={activitySummary.totalOpportunityDifference}
                            comparedTo={activitySummary.comparedTo} activityDate={activityDate} activityType={"opp"} />
                    </Card>
                </Grid>
            </Grid>
        )
    }
    else {
        return <Box>
            <Card variant="outlined">
                <CardContent>
                    <Grid item xs={12} md={12} sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
                        <Card >
                            <Skeleton variant="text" sx={{ fontSize: 16, ml: 3, mr: 3 }} width={200} />
                        </Card>
                    </Grid>
                    <Grid container spacing={1} justifyContent={'left'}>
                        <Grid item xs={12} md={2.4} sx={{ display: 'flex', mb: 0.7, alignItems: 'left' }}>
                            <Skeleton variant="rectangular" width={'100%'} height={90} />
                        </Grid>
                        <Grid item xs={12} md={2.4} sx={{ display: 'flex', mb: 0.7, alignItems: 'left' }}>
                            <Skeleton variant="rectangular" width={'100%'} height={90} />
                        </Grid>
                        <Grid item xs={12} md={2.4} sx={{ display: 'flex', mb: 0.7, alignItems: 'left' }}>
                            <Skeleton variant="rectangular" width={'100%'} height={90} />
                        </Grid>
                        <Grid item xs={12} md={2.4} sx={{ display: 'flex', mb: 0.7, alignItems: 'left' }}>
                            <Skeleton variant="rectangular" width={'100%'} height={90} />
                        </Grid>
                        <Grid item xs={12} md={2.4} sx={{ display: 'flex', mb: 0.7, alignItems: 'left' }}>
                            <Skeleton variant="rectangular" width={'100%'} height={90} />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        </Box>
    }

}

export { DashboardPerformance }
export default DashboardPerformance
